/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {IEventEmitter} from "./IEventEmitter";
import {Listener} from "./Listener";

/** @template Payload */
export class EventEmitter<Payload> implements IEventEmitter<Payload> {
  private readonly listeners: Listener<Payload>[];
  private listenersOnce: Listener<Payload>[];

  /**
   * Create a new EventEmitter
   */
  constructor() {
    this.listeners = [];
    this.listenersOnce = [];
    this.emit = this.emit.bind(this) as (payload: Payload) => void;
    this.on = this.on.bind(this) as (listener: Listener<Payload>) => void;
    this.once = this.once.bind(this) as (listener: Listener<Payload>) => void;
  }

  /**
   * Emits the event to its subscribers
   * @param {Payload} payload
   */
  public emit(payload: Payload) {
    for (const listener of [...this.listeners, ...this.listenersOnce]) {
      listener(payload);
    }
    this.listenersOnce = [];
  }

  /**
   * Unsubscribes the listener from the event
   * @param {Listener<Payload>} listener
   */
  public off(listener: Listener<Payload>) {
    const listenerIndex = this.listeners.indexOf(listener);
    if (listenerIndex > -1) {
      this.listeners.splice(listenerIndex, 1);
    }
  }

  /**
   * Subscribes a listener to the event
   * @param {Listener<Payload>} listener
   */
  public on(listener: Listener<Payload>) {
    this.listeners.push(listener);
  }

  /**
   * Subscribes the listener to receive the event exactly once
   * @param {Listener<Payload>} listener
   */
  public once(listener: Listener<Payload>) {
    this.listenersOnce.push(listener);
  }
}
